/**
 * Header.js - Main navigation header (Redesigned)
 * 
 * Features:
 * - Logo with title and context-aware subtitle
 * - Tab navigation for admin pages (Dashboard, Schedule, Users, Rooms)
 * - User info with role badge
 * - Settings gear icon with dropdown menu (My Profile, Audit Log)
 * - Dark mode toggle icon
 * - Sign out button
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useIsMobile } from '../hooks/useIsMobile';
import MobileNavDrawer from './MobileNavDrawer';

// Custom hook for online/offline detection
function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  return isOnline;
}

function Header() {
  const { user, logout, isAdmin, isSuperAdmin } = useAuth();
  const { darkMode, toggleDarkMode, theme, colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const isOnline = useOnlineStatus();
  
  // State for settings dropdown
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);
  
  // State for mobile navigation drawer
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setSettingsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Determine current section for subtitle and active tab
  const getSubtitle = () => {
    const path = location.pathname;
    if (path === '/admin') return 'Admin Dashboard';
    if (path.includes('/student-schedule')) return 'Student Scheduling';
    if (path.includes('/schedule')) return 'Teacher Scheduling';
    if (path.includes('/users')) return 'User Management';
    if (path.includes('/pto')) return 'PTO Management';
    if (path.includes('/rooms')) return 'Room Management';
    if (path.includes('/students')) return 'Student Management';
    if (path.includes('/audit')) return 'Audit Log';
    if (path.includes('/availability')) return 'My Availability';
    if (path.includes('/profile')) return 'My Profile';
    if (isAdmin) return 'Admin Dashboard';
    return 'Teacher Dashboard';
  };

  // Check if a tab is active
  const isTabActive = (tabPath) => {
    if (tabPath === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.includes(tabPath);
  };

  // =============================================
  // STYLES
  // =============================================
  const headerContainerStyle = {
    backgroundColor: theme.cardBackground,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    position: 'sticky',
    top: 0,
    zIndex: 100
  };

  const headerMainStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 24px',
    backgroundColor: theme.headerBackground
  };

  const logoSectionStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer'
  };

  const logoImageStyle = {
    width: '36px',
    height: '36px',
    objectFit: 'contain'
  };

  const titleContainerStyle = {
    display: 'flex',
    flexDirection: 'column'
  };

  const titleStyle = {
    fontSize: '1.2rem',
    fontWeight: '600',
    color: '#fff',
    margin: 0,
    lineHeight: 1.2
  };

  const subtitleStyle = {
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.8)',
    margin: 0
  };

  // Tab navigation styles (inline in header)
  const tabNavStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginLeft: '30px'
  };

  const tabStyle = (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: isActive ? 'rgba(255,255,255,0.25)' : 'transparent',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: isActive ? '600' : '500',
    transition: 'all 0.2s ease'
  });

  const leftSectionStyle = {
    display: 'flex',
    alignItems: 'center'
  };

  const rightSectionStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  };

  const userInfoStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: '20px'
  };

  const userNameStyle = {
    fontSize: '0.9rem',
    fontWeight: '500',
    color: '#fff'
  };

  const roleBadgeStyle = {
    fontSize: '0.7rem',
    fontWeight: '600',
    padding: '2px 8px',
    borderRadius: '10px',
    backgroundColor: isSuperAdmin ? (colors.purple || '#9c27b0') : isAdmin ? colors.blue : colors.green,
    color: (!isAdmin && darkMode) ? '#1a1a2e' : '#fff',
    textTransform: 'uppercase'
  };

  const iconButtonStyle = {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,0.3)',
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.1rem',
    transition: 'all 0.2s ease'
  };

  // Hamburger menu button for mobile
  const hamburgerButtonStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '44px',
    height: '44px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '8px',
    cursor: 'pointer',
    padding: '8px',
    gap: '5px'
  };

  const hamburgerLineStyle = {
    width: '22px',
    height: '3px',
    backgroundColor: '#fff',
    borderRadius: '2px',
    transition: 'all 0.3s ease'
  };

  const signOutButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '500',
    transition: 'all 0.2s ease'
  };

  // Settings dropdown styles
  const settingsContainerStyle = {
    position: 'relative'
  };
  
  const settingsDropdownStyle = {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '8px',
    backgroundColor: theme.cardBackground,
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    minWidth: '180px',
    overflow: 'hidden',
    zIndex: 200
  };
  
  const dropdownItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    color: theme.text,
    fontSize: '0.9rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    border: 'none',
    backgroundColor: 'transparent',
    width: '100%',
    textAlign: 'left'
  };
  
  const dropdownDividerStyle = {
    height: '1px',
    backgroundColor: theme.border,
    margin: '4px 0'
  };

  // =============================================
  // RENDER
  // =============================================
  
  // Admin tabs - Dashboard, Students, Users, Student Schedule, Teacher Schedule, Rooms, PTO
  const adminTabs = (
    <div style={tabNavStyle}>
      <button 
        style={tabStyle(isTabActive('/admin'))}
        onClick={() => navigate('/admin')}
      >
        <span>🏠</span>
        Dashboard
      </button>
      <button 
        style={tabStyle(isTabActive('/students'))}
        onClick={() => navigate('/admin/students')}
      >
        <span>📋</span>
        Students
      </button>
      <button 
        style={tabStyle(isTabActive('/users'))}
        onClick={() => navigate('/admin/users')}
      >
        <span>👥</span>
        Users
      </button>
      <button 
        style={tabStyle(isTabActive('/student-schedule'))}
        onClick={() => navigate('/admin/student-schedule')}
      >
        <span>📅</span>
        Student Sched
      </button>
      <button 
        style={tabStyle(isTabActive('/schedule') && !location.pathname.includes('student-schedule'))}
        onClick={() => navigate('/admin/schedule')}
      >
        <span>👩‍🏫</span>
        Teacher Sched
      </button>
      <button 
        style={tabStyle(isTabActive('/templates'))}
        onClick={() => navigate('/admin/templates')}
      >
        <span>📋</span>
        Templates
      </button>
      <button 
        style={tabStyle(isTabActive('/rooms'))}
        onClick={() => navigate('/admin/rooms')}
      >
        <span>🏫</span>
        Rooms
      </button>
      <button 
        style={tabStyle(isTabActive('/pto'))}
        onClick={() => navigate('/admin/pto')}
      >
        <span>🏖️</span>
        PTO
      </button>
    </div>
  );

  // Teacher tabs
  const teacherTabs = (
    <div style={tabNavStyle}>
      <button 
        style={tabStyle(location.pathname === '/dashboard')}
        onClick={() => navigate('/dashboard')}
      >
        <span>📅</span>
        Schedule
      </button>
      <button 
        style={tabStyle(isTabActive('/availability'))}
        onClick={() => navigate('/availability')}
      >
        <span>📋</span>
        Availability
      </button>
      <button 
        style={tabStyle(isTabActive('/pto'))}
        onClick={() => navigate('/pto')}
      >
        <span>🏖️</span>
        PTO
      </button>
    </div>
  );

  return (
    <>
      {/* Mobile Navigation Drawer */}
      <MobileNavDrawer 
        isOpen={mobileNavOpen} 
        onClose={() => setMobileNavOpen(false)} 
      />

      <div style={headerContainerStyle}>
        {/* Main Header Row */}
        <div style={headerMainStyle}>
          {/* Left Section - Logo, Title, and Tabs */}
          <div style={leftSectionStyle}>
            {/* Logo and Title */}
            <div 
              style={logoSectionStyle} 
              onClick={() => navigate(isAdmin ? '/admin' : '/dashboard')}
            >
              <img 
                src="/assets/logos/logo-square.png" 
                alt="CPLC Logo" 
                style={logoImageStyle}
              />
              <div style={titleContainerStyle}>
                <h1 style={{...titleStyle, fontSize: isMobile ? '1rem' : '1.2rem'}}>
                  {isMobile ? 'CPLC' : 'Employee Portal'}
                </h1>
                <p style={subtitleStyle}>{getSubtitle()}</p>
              </div>
            </div>

            {/* Tab Navigation (Desktop Only) */}
            {!isMobile && user && isAdmin && adminTabs}
            {!isMobile && user && !isAdmin && teacherTabs}
          </div>

          {/* Right Section - Mobile: Hamburger Only */}
          {isMobile && user && (
            <button 
              style={hamburgerButtonStyle}
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open navigation menu"
            >
              <span style={hamburgerLineStyle}></span>
              <span style={hamburgerLineStyle}></span>
              <span style={hamburgerLineStyle}></span>
            </button>
          )}

          {/* Right Section - Desktop: User Info & Actions */}
          {!isMobile && user && (
          <div style={rightSectionStyle}>
            {/* User Info with Role Badge */}
            <div style={userInfoStyle}>
              <span style={{ fontSize: '1rem' }}>👤</span>
              <span style={userNameStyle}>{user.name}</span>
              <span style={roleBadgeStyle}>
                {isSuperAdmin ? 'Super Admin' : isAdmin ? 'Admin' : 'Teacher'}
              </span>
            </div>

            {/* Settings Gear */}
            <div style={settingsContainerStyle} ref={settingsRef}>
              <button 
                style={{
                  ...iconButtonStyle,
                  backgroundColor: settingsOpen ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)'
                }}
                onClick={() => setSettingsOpen(!settingsOpen)}
                title="Settings"
              >
                ⚙️
              </button>
              
              {/* Settings Dropdown */}
              {settingsOpen && (
                <div style={settingsDropdownStyle}>
                  <button 
                    style={dropdownItemStyle}
                    onClick={() => {
                      navigate('/profile');
                      setSettingsOpen(false);
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = theme.border}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <span>👤</span>
                    My Profile
                  </button>
                  {isAdmin && (
                    <>
                      <div style={dropdownDividerStyle}></div>
                      <button 
                        style={dropdownItemStyle}
                        onClick={() => {
                          navigate('/admin/audit');
                          setSettingsOpen(false);
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = theme.border}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        <span>📋</span>
                        Audit Log
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Dark Mode Toggle */}
            <button 
              style={iconButtonStyle}
              onClick={toggleDarkMode}
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {darkMode ? '☀️' : '🌙'}
            </button>

            {/* Sign Out Button */}
            <button 
              style={signOutButtonStyle}
              onClick={handleLogout}
            >
              <span>↪</span>
              Sign Out
            </button>
          </div>
        )}
      </div>
      
      {/* Offline Indicator */}
      {!isOnline && (
        <div style={{
          backgroundColor: '#f44336',
          color: 'white',
          padding: '8px 16px',
          textAlign: 'center',
          fontSize: '0.85rem',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <span>📡</span>
          No internet connection - The app requires internet to function
        </div>
      )}
    </div>
    </>
  );
}

export default Header;
